// --- React · 라우팅 ---
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import styled from '@emotion/styled';
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Card, SummaryCard, CardTitle, Flex, Text, Spinner } from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { Button, Tag } from '@/components/ui';

// --- API ---
import { useGetAllCompanies, useDeleteCompany } from '@/api/admin';
import { useGetAllUsers } from '@/api/auth';

// --- 유효성 검사 · 에러 파싱 ---
import { getApiErrorKey } from '@/api/core';

// --- 스토어 ---
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useModalStore } from '@/store/modalStore';

// --- 타입 ---
import { isAdminUser, isSuperAdminByRoleId, resolveRoleId } from '@packages/types/auth/roles';
import type { Company } from '@packages/types/company';
import type { User } from '@packages/types/auth';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { formatSubscriptionExpiryLine } from '@/utils/date';

// --- 회사 상세 · 구독 ---
import CompanyAdminDetailPanel from '../management/company/CompanyAdminDetailPanel';
import CompanySubscriptionSettingsModal from '../management/company/CompanySubscriptionSettingsModal';
import type { CompanyUserDisplay } from './CompanyUsersReadOnlyTable';
import AdminInviteModal from './AdminInviteModal';

const TableActionButton = styled(Button)`
  width: auto;
  min-height: 26px;
  height: 26px;
  padding: 0 ${({ theme }) => theme.spacing[3]}px;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  line-height: 1;
  flex-direction: row;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
`;

// ==================== 컴포넌트 ====================

const AdminCompanyManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['company', 'common', 'auth']);
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuthStore();
  const { showError, showSuccess } = useNotificationStore();
  const { showTypedConfirm } = useModalStore();
  const isAdmin = isAdminUser(user);
  const isSuperAdmin = isSuperAdminByRoleId(user ? resolveRoleId(user) : undefined);

  // ==================== 상태 관리 ====================

  const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);
  const [subscriptionModalCompanyId, setSubscriptionModalCompanyId] = useState<number | null>(
    null,
  );
  const [showAdminInviteModal, setShowAdminInviteModal] = useState(false);

  // ==================== API 훅 ====================

  const { data: companies = [], isLoading: loadingCompanies } = useGetAllCompanies({
    enabled: isAuthenticated && isAdmin,
  });
  const { data: allUsers = [] } = useGetAllUsers(1, 1000);
  const deleteCompanyMutation = useDeleteCompany();

  const usersByCompanyId = useMemo(() => {
    const map = new Map<number, CompanyUserDisplay[]>();
    for (const raw of allUsers) {
      const user = raw as User & {
        company?: { id: number; name?: string };
        role?: { name?: string };
        created_at?: string | Date | null;
      };
      const companyId = user.company?.id ?? user.company_id;
      if (!companyId) continue;
      const normalized: CompanyUserDisplay = {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone ?? null,
        status: user.status,
        role_name: user.role?.name ?? user.role_name ?? 'Guest',
        created_at: user.created_at ?? null,
      };
      const list = map.get(companyId) ?? [];
      list.push(normalized);
      map.set(companyId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    }
    return map;
  }, [allUsers]);

  const activeUserCount = useMemo(
    () => allUsers.filter(u => u.status === 'active').length,
    [allUsers],
  );

  // ==================== 이펙트 ====================

  /** 권한 확인 — Admin이 아니면 메인 페이지로 리다이렉트 */
  React.useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!isAdminUser(user)) {
      showError(i18nKeys.company.need_admin_role);
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, showError]);

  // ==================== 핸들러 ====================

  /** 회사 삭제 (confirm 후 API 호출) */
  const handleDeleteCompany = (company: Company) => {
    showTypedConfirm({
      title: t(i18nKeys.company.company_delete),
      message: t(i18nKeys.company.company_delete_confirm),
      confirmKeyword: company.name,
      confirmPrompt: t(i18nKeys.company.delete_type_name_prompt),
      variant: 'danger',
      onConfirm: () => {
        deleteCompanyMutation.mutate(company.id, {
          onSuccess: () => {
            showSuccess(i18nKeys.company.company_delete_success);
            setExpandedCompanyId(null);
          },
          onError: (error: unknown) => {
            const errorKey = getApiErrorKey(error, i18nKeys.company.company_delete_failed);
            showError(errorKey);
          },
        });
      },
    });
  };

  /** 상세보기 영역 토글 */
  const toggleCompanyDetails = (companyId: number) => {
    setExpandedCompanyId(prev => (prev === companyId ? null : companyId));
  };

  /** 날짜 포맷 (ko-KR) */
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // ==================== 렌더링 ====================

  if (loadingCompanies) {
    return (
      <DefaultContents>
        <Flex align="center" justify="center">
          <Spinner size="md" />
        </Flex>
      </DefaultContents>
    );
  }

  // 통계 계산
  const activeCompanies = companies.filter(c => c.status === 'active').length;

  const pageShellStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    gap: theme.spacing[6],
  };

  return (
    <DefaultContents>
      <div style={pageShellStyle}>
      {/* 통계 카드 */}
      <SummaryCard
        items={[
          {
            label: t(i18nKeys.company.total_companies),
            value: companies.length,
            unit: null,
            decimal: null,
            state: null,
          },
          {
            label: '활성 회사',
            value: activeCompanies,
            unit: null,
            decimal: null,
            state: 'success',
          },
          {
            label: t(i18nKeys.auth.totalUsers),
            value: allUsers.length,
            unit: null,
            decimal: null,
            state: null,
          },
          {
            label: t(i18nKeys.auth.activeUsers),
            value: activeUserCount,
            unit: null,
            decimal: null,
            state: 'success',
          },
        ]}
      />

      {/* 회사 목록 — 카드 내부 스크롤 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      <Card fullWidth fullHeight flexGrow={1} layout="flex" gap={4}>
        <Flex justify="between" align="center" style={{ flexShrink: 0 }}>
          <CardTitle>{t(i18nKeys.company.company_list)}</CardTitle>
          {isSuperAdmin && (
            <Button variant="primary" onClick={() => setShowAdminInviteModal(true)}>
              {t(i18nKeys.auth.addAdmin)}
            </Button>
          )}
        </Flex>

        {companies.length === 0 ? (
          <Flex align="center" justify="center" style={{ padding: theme.spacing[7], flex: 1 }}>
            <Text color={theme.colors.text.secondary}>등록된 회사가 없습니다.</Text>
          </Flex>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: theme.fontSize.md,
              }}
              role="table"
              aria-label={t(i18nKeys.company.company_list)}
            >
              {/* 테이블 헤더 */}
              <thead style={{ backgroundColor: theme.colors.surface.header.default }}>
                <tr>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.company_name)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.company_status)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.company_telephone)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.company_location)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.company_created_at)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.auth.usersList)}
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    <Flex direction="column" align="center" gap={1}>
                      <span>{t(i18nKeys.company.max_devices)}</span>
                      <Text size="xs" color="textSecondary">
                        {t(i18nKeys.company.subscription_end_date)}
                      </Text>
                    </Flex>
                  </th>
                  <th
                    style={{
                      padding: theme.spacing[6],
                      textAlign: 'center',
                      fontWeight: theme.fontWeight.semibold,
                      color: theme.colors.text.primary,
                    }}
                  >
                    {t(i18nKeys.company.site_action)}
                  </th>
                </tr>
              </thead>

              {/* 테이블 본문 */}
              <tbody>
                {companies.map(company => {
                  const isExpanded = expandedCompanyId === company.id;
                  const accentColor = theme.colors.border.indicator;
                  const expandedSurfaceBg = theme.colors.surface.header.nested;

                  return (
                  <React.Fragment key={company.id}>
                    {/* 회사 행 */}
                    <tr
                      style={{
                        borderBottom: isExpanded
                          ? 'none'
                          : `1px solid ${theme.colors.border.container}`,
                        textAlign: 'center',
                        backgroundColor: isExpanded ? expandedSurfaceBg : undefined,
                        boxShadow: isExpanded ? `inset 4px 0 0 ${accentColor}` : undefined,
                      }}
                    >
                      <td>
                        <Text align="center" weight="semibold">
                          {company.name}
                        </Text>
                      </td>
                      <td>
                        <Tag variant={company.status === 'active' ? 'success' : 'error'} size="xs">
                          {company.status}
                        </Tag>
                      </td>
                      <td>
                        <Text align="center" size="md">
                          {company.telephone || '-'}
                        </Text>
                      </td>
                      <td>
                        <Text align="center" size="md">
                          {company.location || '-'}
                        </Text>
                      </td>
                      <td>
                        <Text align="center" size="md">
                          {formatDate(company.created_at)}
                        </Text>
                      </td>
                      <td>
                        <Flex justify="center">
                          <Tag variant="primary" size="xs">
                            {t(i18nKeys.auth.peopleCount, {
                              count: usersByCompanyId.get(company.id)?.length ?? 0,
                            })}
                          </Tag>
                        </Flex>
                      </td>
                      <td>
                        <Flex direction="column" align="center" gap={1}>
                          <Text align="center" size="md">
                            {company.max_devices_override != null
                              ? company.max_devices_override
                              : '∞'}
                          </Text>
                          {(() => {
                            const { text, dayNum } = formatSubscriptionExpiryLine(
                              company.subscription_end,
                            );
                            const color =
                              dayNum === null
                                ? 'textSecondary'
                                : dayNum < 0
                                  ? 'error'
                                  : dayNum <= 7
                                    ? 'warning'
                                    : 'textSecondary';
                            return (
                              <Text align="center" size="xs" color={color}>
                                {text}
                              </Text>
                            );
                          })()}
                        </Flex>
                      </td>
                      <td
                        onClick={e => e.stopPropagation()}
                        style={{ padding: `${theme.spacing[3]}px ${theme.spacing[4]}px` }}
                      >
                        <Flex gap={1} align="center" justify="center" wrap>
                          <TableActionButton
                            variant="secondary"
                            onClick={() => setSubscriptionModalCompanyId(company.id)}
                          >
                            {t(i18nKeys.company.subscription_settings_open)}
                          </TableActionButton>
                          <TableActionButton
                            variant="secondary"
                            onClick={() => toggleCompanyDetails(company.id)}
                          >
                            {isExpanded
                              ? t(i18nKeys.company.close)
                              : t(i18nKeys.company.company_row_view_details)}
                          </TableActionButton>
                          <TableActionButton
                            onClick={() => handleDeleteCompany(company)}
                            disabled={deleteCompanyMutation.isPending}
                          >
                            {t(i18nKeys.auth.delete)}
                          </TableActionButton>
                        </Flex>
                      </td>
                    </tr>

                    {/* 상세보기 (확장 시) */}
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            padding: 0,
                            backgroundColor: expandedSurfaceBg,
                            boxShadow: `inset 4px 0 0 ${accentColor}`,
                            borderBottom: `1px solid ${theme.colors.border.container}`,
                          }}
                        >
                          <div style={{ padding: theme.spacing[6] }}>
                            {company.description && (
                              <div style={{ marginBottom: theme.spacing[4] }}>
                                <Text size="sm" weight="semibold">
                                  {t(i18nKeys.company.company_description)}:
                                </Text>
                                <Text size="sm"> {company.description}</Text>
                              </div>
                            )}

                            <CompanyAdminDetailPanel
                              companyId={company.id}
                              users={usersByCompanyId.get(company.id) ?? []}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      </div>
      </div>

      <CompanySubscriptionSettingsModal
        isOpen={subscriptionModalCompanyId != null}
        companyId={subscriptionModalCompanyId}
        onClose={() => setSubscriptionModalCompanyId(null)}
      />

      <AdminInviteModal isOpen={showAdminInviteModal} onClose={() => setShowAdminInviteModal(false)} />
    </DefaultContents>
  );
};

export default AdminCompanyManagement;
